import {
  DRILL_TRAY_DEFAULT,
  type DrillCell,
  DrillMenu,
  DrillTray,
  Pager,
  ReportFooter,
  ReportView,
  SHOW_AS_LABELS,
  type Transport,
  useReportQuery,
} from '@clockwise/report-kit';
import {
  DEFAULT_DETAIL_LIMIT,
  findMeasure,
  planQuery,
  type ReportModel,
  type ReportSpec,
} from '@clockwise/semantic-core';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Download, SquareArrowOutUpRight } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { analyticsApi, reportDefinitionsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';
import { parkSpec } from '@/lib/report-handoff';
import {
  coordinateWords,
  type DrillStep,
  ENTRIES,
  entriesName,
  modelFor,
  planMenu,
  resolveChain,
  type TargetDefinition,
  targetDetail,
  trayChips,
} from './drill-chain';

export interface UseReportDrillArgs {
  models: readonly ReportModel[] | undefined;
  /** The report the reader is on: its saved id (none for a draft), name and live spec. */
  source: { id?: string; name: string; spec: ReportSpec | null };
  transport: Transport;
  /** The chain, owned by the host — the URL on a saved report, state on a draft. */
  chain: readonly DrillStep[];
  onChainChange: (chain: DrillStep[]) => void;
  /** May build reports — the one thing "Open as its own report" needs. */
  mayBuild: boolean;
}

/** An open menu: the click, and how deep in the chain it was made. */
interface OpenMenu {
  cell: DrillCell;
  /** 0 = on the report itself; n = inside the tray, at level n. */
  depth: number;
}

/**
 * Drill-down on a custom report: the menu a click opens, and the tray a pick
 * docks under the report.
 *
 * DRILL STATE IS NOT THE REPORT. It never touches the builder's draft, so a
 * reader who drills has not edited anything and the unsaved-changes prompt
 * (#795) stays quiet. On a saved report the chain lives in the URL
 * (`?drill=`), so a drill is a link; on `/reports/new` there is no saved report
 * for the link to start from, so it lives in memory and is not shareable.
 *
 * Nothing here decides anything. Which reports a click may land on is
 * `drillEligibility`, per click; what the landing asks is `landingSpec`. This
 * fetches what those need and draws what they answer.
 */
export function useReportDrill({
  models,
  source,
  transport,
  chain,
  onChainChange,
  mayBuild,
}: UseReportDrillArgs): {
  onDrill?: (cell: DrillCell) => void;
  tray?: ReactNode;
  menu?: ReactNode;
} {
  const navigate = useNavigate();
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const [height, setHeight] = useState(DRILL_TRAY_DEFAULT);
  const menuToggle = useRef<HTMLElement | null>(null);
  // The cell a pick was made from. The tray takes most of the column when it
  // opens, and the row the reader clicked must still be on screen above it —
  // otherwise the figure they are checking the landing against is gone.
  const reveal = useRef<HTMLElement | null>(null);
  // Runs when the chain changes, which is when the tray moves.
  useEffect(() => {
    const el = reveal.current;
    reveal.current = null;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      // The TABLE's own scroll box, never the page: in a stacked layout a
      // page-level scroll would push the tray just opened below the fold.
      const box = el.isConnected ? el.closest<HTMLElement>('.rk-table-scroll') : null;
      if (!box) return;
      const cell = el.getBoundingClientRect();
      const view = box.getBoundingClientRect();
      box.scrollTop += cell.top - view.top - (view.height - cell.height) / 2;
    });
    return () => cancelAnimationFrame(id);
  }, [chain]);

  // Memoised: with a basis, `modelFor` lends a NEW model object each call, and
  // this is a dependency of the chain's replay below.
  const sourceModel = useMemo(
    () => (models && source.spec ? modelFor(models, source.spec) : undefined),
    [models, source.spec],
  );
  const drillable = !!sourceModel?.drillTo;

  // Once per page: which saved reports this viewer could land on at all. The
  // per-click narrowing is local, so a second click costs no request.
  const candidates = useQuery({
    queryKey: queryKeys.reportDefinitions.drillTargets(),
    queryFn: () => reportDefinitionsApi.drillTargets().then((r) => r.data),
    enabled: drillable,
    staleTime: 60_000,
  });

  // The definitions of the saved reports in the chain — the same cache entry
  // opening each one would use.
  const reportIds = [...new Set(chain.filter((s) => s.target !== ENTRIES).map((s) => s.target))];
  const definitions = useQueries({
    queries: reportIds.map((id) => ({
      queryKey: queryKeys.reportDefinitions.detail(id),
      queryFn: () => reportDefinitionsApi.get(id).then((r) => r.data),
      staleTime: 30_000,
      retry: false,
    })),
  });
  const definition = (id: string): TargetDefinition => {
    const q = definitions[reportIds.indexOf(id)];
    if (!q || q.isPending) return 'pending';
    if (q.isError || !q.data) return 'missing';
    return { name: q.data.name, spec: q.data.spec };
  };

  // `useQueries` hands back a NEW array on every render, so keying on it would
  // replay the whole chain — and re-project the tray's pivot — on every
  // keystroke in the builder above. Keyed on what the definitions SAY instead.
  const definitionsKey = definitions.map((q) => `${q.status}:${q.dataUpdatedAt}`).join('|');
  const resolved = useMemo(
    () =>
      models && sourceModel && source.spec
        ? resolveChain(models, { model: sourceModel, spec: source.spec }, chain, definition)
        : { levels: [], pending: chain.length > 0 },
    // `definition` reads the queries `definitionsKey` summarises.
    [models, sourceModel, source.spec, chain, definitionsKey],
  );
  const top = resolved.levels[resolved.levels.length - 1];

  // LABELS ARE NOT IN THE URL (they are names). A click made here remembers
  // what it read; a link opened fresh asks for exactly the keys it pins, on the
  // model each one was pinned on — the same lookup a saved filter's chip uses.
  const labelCache = useRef(new Map<string, string>());
  const labelKey = (dim: string, key: string) => `${dim}\u0000${key}`;
  const unlabelled = chain.flatMap((step, i) => {
    const model = i === 0 ? sourceModel : resolved.levels[i - 1]?.model;
    return Object.entries(step.at).flatMap(([dim, key]) =>
      key !== null && model && !step.labels[dim] && !labelCache.current.has(labelKey(dim, key))
        ? [{ model: model.id, dim, key }]
        : [],
    );
  });
  const lookedUp = useQueries({
    queries: unlabelled.map((u) => ({
      queryKey: [...queryKeys.analytics.all, 'drill-label', u.model, u.dim, u.key],
      queryFn: () =>
        analyticsApi
          .dimensionValues(u.model, u.dim, {
            from: source.spec?.from ?? '',
            to: source.spec?.to ?? '',
            keys: [u.key],
          })
          .then((r) => r.data[0]?.label ?? null),
      enabled: !!source.spec,
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    })),
  });
  for (const [i, u] of unlabelled.entries()) {
    const label = lookedUp[i]?.data;
    if (label) labelCache.current.set(labelKey(u.dim, u.key), label);
  }
  /** The chain with every pin's label filled in, as far as it is known. */
  const labelled = chain.map((step) => ({
    ...step,
    labels: Object.fromEntries(
      Object.entries(step.at).map(([dim, key]) => [
        dim,
        step.labels[dim] ??
          (key === null ? 'no value' : (labelCache.current.get(labelKey(dim, key)) ?? '…')),
      ]),
    ),
  }));

  // The last row count the listing reported, so the pager keeps its place
  // (and stays mounted) while the next page is in flight.
  const lastTotal = useRef<{ for: string; total?: number }>({ for: '' });

  // THE LANDING'S OWN QUERY. A listing pages, so the tray keeps its own offset
  // — reset whenever the landing itself changes.
  // Keyed by CONTENT: the landing is recomputed whenever the report above it
  // publishes a draft, and a new object asking the same question must not
  // send the reader back to page one.
  const landingKey = top ? JSON.stringify(top.landing.spec) : '';
  const [page, setPage] = useState({ for: '', offset: 0, size: DEFAULT_DETAIL_LIMIT });
  const paging = page.for === landingKey ? page : { for: landingKey, offset: 0, size: page.size };
  const landingSpec = useMemo(
    () =>
      top && top.landing.spec.kind === 'detail'
        ? { ...top.landing.spec, offset: paging.offset || undefined, limit: paging.size }
        : top?.landing.spec,
    [top, paging.offset, paging.size],
  );
  const plan = useMemo(
    () => (top && landingSpec ? planQuery(top.model, landingSpec) : null),
    [top, landingSpec],
  );
  const landed = useReportQuery(landingSpec ?? null, plan, transport);

  const exportLanding = useMutation({
    mutationFn: () => {
      if (!top) throw new Error('Nothing to export');
      // The LANDING first: both the server's cap and the filename's cut keep
      // the front, and the landing is what tells one drill's file from another
      // drill's off the same report.
      return analyticsApi.exportReport(top.landing.spec, { name: `${top.name} — ${source.name}` });
    },
    onSuccess: ({ blob, filename }) => triggerDownload(blob, filename ?? 'drill-down.xlsx'),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not export this drill-down'),
  });

  if (!models || !sourceModel || !source.spec || !drillable) return {};
  const spec = source.spec;

  /** Where a click at `depth` was made: the report, or a landing in the tray. */
  const originAt = (depth: number) => {
    const level = depth > 0 ? resolved.levels[depth - 1] : undefined;
    return level ? { model: level.model, spec: level.landing.spec } : { model: sourceModel, spec };
  };

  const pick = (open: OpenMenu, target: string) => {
    setMenu(null);
    reveal.current = open.cell.returnFocus;
    for (const [dim, label] of Object.entries(open.cell.labels)) {
      const key = open.cell.at[dim];
      if (key !== null && key !== undefined) labelCache.current.set(labelKey(dim, key), label);
    }
    onChainChange([
      ...chain.slice(0, open.depth),
      {
        target,
        at: open.cell.at,
        labels: open.cell.labels,
        measure: open.cell.measureId,
        figure: open.cell.text,
        ...(open.cell.showAs ? { showAs: open.cell.showAs } : {}),
      },
    ]);
  };

  let menuNode: ReactNode;
  if (menu) {
    const origin = originAt(menu.depth);
    const pinned = [
      ...new Set([
        ...chain.slice(0, menu.depth).flatMap((s) => Object.keys(s.at)),
        ...Object.keys(menu.cell.at),
      ]),
    ];
    const reportChain = [
      source.id ?? '__draft',
      ...chain.slice(0, menu.depth).flatMap((s) => (s.target === ENTRIES ? [] : [s.target])),
    ];
    const plan = planMenu(models, candidates.data ?? [], { ...origin, pinned, chain: reportChain });
    const open = menu;
    menuNode = (
      <DrillMenu
        heading={menu.depth === 0 ? 'Drill into' : 'Drill deeper'}
        coordinate={coordinateWords(menu.cell.labels)}
        entries={
          plan.entries
            ? {
                key: ENTRIES,
                label: 'Show underlying entries',
                detail: `The ${entriesName(models, origin.model.drillTo?.report).toLowerCase()} behind this figure.`,
                badge: 'ties out',
                onSelect: () => pick(open, ENTRIES),
              }
            : undefined
        }
        targets={plan.targets.map((c) => ({
          key: c.id,
          label: c.label,
          title: c.label === c.name ? undefined : c.name,
          detail: targetDetail(models, c),
          onSelect: () => pick(open, c.id),
        }))}
        targetsNote={
          candidates.isPending
            ? 'Looking for saved reports…'
            : candidates.isError
              ? 'Couldn’t load saved reports — only the entries are offered.'
              : undefined
        }
        anchor={menu.cell.anchor}
        toggleRef={menuToggle}
        onClose={() => {
          setMenu(null);
          open.cell.returnFocus?.focus();
        }}
      />
    );
  }

  const openMenu = (depth: number) => (cell: DrillCell) => {
    menuToggle.current = cell.returnFocus;
    setMenu({ cell, depth });
  };

  let trayNode: ReactNode;
  if (chain.length > 0) {
    const levels = resolved.levels;
    const depth = levels.length;
    const previous = depth > 1 ? levels[depth - 2] : undefined;
    const clickedOn = originAt(Math.max(0, depth - 1));
    const measure = top ? findMeasure(clickedOn.model, top.step.measure) : undefined;
    const data = landed.data;
    if (data?.meta.totalRows !== undefined) {
      lastTotal.current = { for: landingKey, total: data.meta.totalRows };
    }
    const total = lastTotal.current.for === landingKey ? lastTotal.current.total : undefined;
    trayNode = (
      <DrillTray
        lineage={[source.name, ...levels.map((l) => l.name)]}
        title={top?.name ?? 'Opening…'}
        note={
          top && top.landing.spec.kind !== 'detail'
            ? `Layout, columns and sort from ${top.name}. The question — filters and period — from the cell you clicked; its own filters don’t apply.`
            : undefined
        }
        figure={{
          label: `${measure ? measure.label : 'Figure you clicked'}${
            top?.step.showAs ? ` (${SHOW_AS_LABELS[top.step.showAs]})` : ''
          }`,
          text: top?.step.figure ?? chain[chain.length - 1]?.figure ?? '',
        }}
        chips={trayChips(models, labelled.slice(0, Math.max(depth, 1)))}
        back={
          depth > 1
            ? {
                label: `Back to ${previous?.name ?? 'the previous level'}`,
                onBack: () => onChainChange(chain.slice(0, -1)),
              }
            : undefined
        }
        onClose={() => {
          setMenu(null);
          onChainChange([]);
        }}
        height={height}
        onResize={setHeight}
        actions={
          top && (
            <>
              {/* The same control as the report's own Export, so the two read
                  as one action at two levels. */}
              <Button
                type="button"
                variant="outline"
                disabled={exportLanding.isPending}
                onClick={() => exportLanding.mutate()}
              >
                <Download className="size-4" />
                {exportLanding.isPending ? 'Exporting…' : 'Export'}
              </Button>
              {mayBuild && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const nonce = parkSpec(top.landing.spec);
                    if (!nonce) {
                      toast.error('Could not open this as a report in this browser.');
                      return;
                    }
                    navigate({ to: '/reports/new', search: { from: nonce } });
                  }}
                >
                  <SquareArrowOutUpRight className="size-4" />
                  Open as its own report
                </Button>
              )}
            </>
          )
        }
      >
        {resolved.broken ? (
          <p className="rk-error">{resolved.broken}</p>
        ) : !top || !landingSpec ? (
          <p className="rk-empty">Running…</p>
        ) : landed.error ? (
          <p className="rk-error">{landed.error.message}</p>
        ) : (
          <>
            {landed.loading || !data ? (
              <p className="rk-empty">Running…</p>
            ) : (
              <>
                <ReportView
                  model={top.model}
                  spec={landingSpec}
                  result={data}
                  plan={plan ?? undefined}
                  // A pivot landing drills again; a listing IS the rows.
                  onDrill={openMenu(depth)}
                />
                {landingSpec.kind !== 'detail' && (
                  <ReportFooter
                    model={top.model}
                    spec={landingSpec}
                    result={data}
                    plan={plan ?? undefined}
                  />
                )}
              </>
            )}
            {/* OUTSIDE the loading branch, as the builder's is: inside it, a
                page turn unmounted the control just pressed and `busy` could
                never be true. */}
            {landingSpec.kind === 'detail' && (
              <Pager
                offset={paging.offset}
                pageSize={paging.size}
                shown={data?.rows.length ?? 0}
                total={data?.meta.totalRows ?? total}
                busy={landed.loading}
                pastEnd={data?.meta.pastEnd}
                onPage={(offset) => setPage({ for: landingKey, offset, size: paging.size })}
                onPageSize={(size) => setPage({ for: landingKey, offset: 0, size })}
              />
            )}
          </>
        )}
      </DrillTray>
    );
  }

  return { onDrill: openMenu(0), tray: trayNode, menu: menuNode };
}
